import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, merge, Observable } from 'rxjs';
import { debounceTime, finalize, first, map, skip, switchMap, tap } from 'rxjs/operators';
import { Permission } from 'src/app/common/enums';
import { Messages } from 'src/app/common/enums/messages';
import { AbsenceService } from 'src/app/data/absence.service';
import { AbsencesRefreshService } from 'src/app/absences/services/absences-refresh.service';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { WorkingTimePeriodsDto } from 'src/app/models/dtos/absences/working-time-periods-dto';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { AbsenceType } from 'src/app/models/enums/absence-type';
import { WorkerProfile } from 'src/app/models/WorkerProfile';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { AbsenceModalComponent } from '../absence-form/absence-form.component';
import { SickLeaveModalComponent } from '../sick-leaves-modal/sick-leaves-modal.component';
import { AuthService } from './../../core/authentication/auth.service';
import { WorkerAbsencesListDataSource } from './worker-absences-list.datasource';

@Component({
  selector: 'app-worker-absences-list',
  templateUrl: './worker-absences-list.component.html',
  styleUrls: ['./worker-absences-list.component.scss'],
  providers: [DatePipe, WorkerAbsencesListDataSource],
  standalone: false
})
export class WorkerAbsencesListComponent implements OnInit, OnChanges, AfterViewInit {
  dataSource = inject(WorkerAbsencesListDataSource, { self: true });
  private absenceService = inject(AbsenceService);
  private dictionaryService = inject(DictionaryService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private snackbar = inject(SnackBarService);
  private authService = inject(AuthService);
  private datePipe = inject(DatePipe);
  private refreshService = inject(AbsencesRefreshService);

  private readonly destroyRef = inject(DestroyRef);

  profile!: WorkerProfile;
  isActionInProgress = false;
  private readonly baseColumns = ['employer', 'employerObject', 'absenceType', 'sentToApprovalOn', 'startDate', 'endDate'];
  private readonly tailColumns = ['businessDays', 'absenceReason', 'status', 'actions'];
  displayedColumns: string[] = [...this.baseColumns, ...this.tailColumns];
  displayedColumnsMobile = ['absenceType', 'date', 'businessDays', 'status', 'actions'];

  absenceTypes$!: Observable<DictionaryItem[]>;
  absenceStatuses$!: Observable<DictionaryItem[]>;
  filterTypeIds = new FormControl<number[]>([], { nonNullable: true });
  filterStatusIds = new FormControl<number[]>([], { nonNullable: true });
  currentYear = new Date().getFullYear();

  onYearChange = (year: number): void => {
    this.currentYear = year;
    this.yearChange.emit(year);
    this.paginator.pageIndex = 0;
    this.fetchAbsences();
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @Input() workerAgreementId!: number | null;
  @Input() year: number | null = null;
  /** Periods of the selected agreement, reused by the absence form instead of fetching them again. */
  @Input() workingTimePeriods: WorkingTimePeriodsDto | null = null;
  @Output() yearChange = new EventEmitter<number>();

  private readonly refreshToken$ = toObservable(this.refreshService.refreshToken);

  ngOnChanges(changes: SimpleChanges): void {
    const yearChanged = changes.year?.currentValue != null && changes.year.currentValue !== this.currentYear;

    if (changes.year?.currentValue != null && changes.year.currentValue !== this.currentYear) {
      this.currentYear = changes.year.currentValue;
    }

    const shouldReload = !!changes.workerAgreementId?.currentValue || yearChanged;
    if (shouldReload && this.paginator && this.sort) {
      this.fetchAbsences();
    }
  }

  ngOnInit(): void {
    if (this.year != null) {
      this.currentYear = this.year;
    }

    this.route.parent!.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => (this.profile = data['workerProfile']));

    this.absenceTypes$ = this.dictionaryService.getAbsenceTypes();
    this.absenceStatuses$ = this.dictionaryService
      .getAbsenceStatuses()
      .pipe(map((statuses) => statuses.filter((s) => s.Id !== AbsenceStatus.Draft)));
  }

  ngAfterViewInit(): void {
    this.sort.sortChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.paginator.pageIndex = 0));
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.paginator.pageIndex = 0));

    merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange)
      .pipe(tap(() => this.fetchAbsences()), takeUntilDestroyed(this.destroyRef))
      .subscribe();

    merge(this.filterTypeIds.valueChanges, this.filterStatusIds.valueChanges)
      .pipe(
        debounceTime(0),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.fetchAbsences();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.dataSource.absences$
      .pipe(
        tap((absences) => {
          const hasPartTime = absences.some((a) => a.RequestedDays !== null);
          this.displayedColumns = hasPartTime
            ? [...this.baseColumns, 'requestedDays', ...this.tailColumns]
            : [...this.baseColumns, ...this.tailColumns];
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.refreshToken$
      .pipe(
        skip(1),
        tap(() => this.fetchAbsences()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.dataSource.absencesSubject.next({
      WorkerId: this.profile.WorkerId,
      WorkerAgreementId: this.workerAgreementId,
      Page: 1,
      PageSize: 10,
      SortingField: 'StartDate',
      SortingDirection: 'desc',
      AbsenceTypeIds: [],
      AbsenceStatusIds: [],
      Year: this.currentYear,
    });
  }

  reload(): void {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.fetchAbsences();
  }

  private refreshAndComplete() {
    return (source$) => source$.pipe(
      tap(() => this.refreshService.refresh()),
      finalize(() => (this.isActionInProgress = false)),
    );
  }

  openModal(record?: AbsenceDto) {
    this.dialog
      .open(AbsenceModalComponent, {
        maxHeight: '90vh',
        data: {
          WorkerId: this.profile.WorkerId,
          Absence: record,
          WorkerAgreementId: record!.WorkerAgreementId,
          EmploymentTypeId: record!.EmploymentTypeId,
          WorkingTimePeriods: record!.WorkerAgreementId === this.workerAgreementId ? this.workingTimePeriods : null,
        },
      })
      .afterClosed()
      .pipe(first())
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.refreshService.refresh();
        }
      });
  }

  isSendToApprovalButtonVisible = (absenceStatusId: number, isApprovalRequired: boolean): boolean =>
    isApprovalRequired && absenceStatusId === AbsenceStatus.Draft;

  sendToApproval(absenceId: number): void {
    const onConfirm = (absenceId: number) => {
      this.isActionInProgress = true;
      return this.absenceService.sendToApproval(absenceId).pipe(
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullySentToApprovalAbsence)),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmSendingToApprovalAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
        this.refreshAndComplete(),
      )
      .subscribe();
  }

  isConfirmAbsenceButtonVisible = (absenceStatusId: number, isApprovalRequired: boolean): boolean =>
    !isApprovalRequired && absenceStatusId === AbsenceStatus.Draft;

  confirmAbsence(absenceId: number) {
    const onConfirm = (absenceId: number) => {
      this.isActionInProgress = true;
      return this.absenceService.confirmAbsence(absenceId).pipe(
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyConfirmedAbsence)),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmConfirmingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
        this.refreshAndComplete(),
      )
      .subscribe();
  }

  isDeleteButtonVisible = (absenceStatusId: number) => absenceStatusId === AbsenceStatus.Draft;

  deleteAbsence(absenceId: number) {
    const onConfirm = (absenceId: number) => {
      this.isActionInProgress = true;
      return this.absenceService.deleteAbsence(absenceId).pipe(
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyDeletedAbsence)),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmDeletingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
        this.refreshAndComplete(),
      )
      .subscribe();
  }

  isBackToDraftButtonVisible = (absenceStatusId: number) => absenceStatusId === AbsenceStatus.PendingApproval;

  backToDraftAbsence(absenceId: number) {
    const onConfirm = (absenceId: number) => {
      this.isActionInProgress = true;
      return this.absenceService.backToDraftAbsence(absenceId).pipe(
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyBackToDraftAbsence)),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmBackToDraftAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
        this.refreshAndComplete(),
      )
      .subscribe();
  }

  isAcceptAbsenceButtonVisible = (absenceStatusId: number) => absenceStatusId === AbsenceStatus.PendingApproval && this.authService.authServerUserId != this.profile.AuthServerUserId;

  acceptAbsence(absenceId: number) {
    const onConfirm = () => {
      this.isActionInProgress = true;
      return this.absenceService.acceptAbsence(absenceId).pipe(
        first(),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccesfullyAcceptedAbsence)),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmAcceptingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        this.refreshAndComplete(),
      )
      .subscribe();
  }

  isRejectAbsenceButtonVisible = (absenceStatusId: number) => absenceStatusId === AbsenceStatus.PendingApproval && this.authService.authServerUserId != this.profile.AuthServerUserId;

  rejectAbsence(absenceId: number) {
    const onConfirm = () => {
      this.isActionInProgress = true;
      return this.absenceService.rejectAbsence(absenceId).pipe(
        first(),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccesfullyRejectedAbsence)),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmRejectingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        this.refreshAndComplete(),
      )
      .subscribe();
  }

  isCancelAbsenceButtonVisible = (absenceStatusId: number, absenceTypeId: number): boolean => absenceStatusId === AbsenceStatus.Accepted && absenceTypeId !== AbsenceType.SickLeave;

  cancelAbsence(absenceId: number): void {
    const onConfirm = () => {
      this.isActionInProgress = true;
      return this.absenceService.cancelAbsence(absenceId).pipe(
        first(),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyCanceledAbsence)),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmCancellingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        this.refreshAndComplete(),
      )
      .subscribe();
  }

  openSickLeaveDetailsModal(absenceId: number) {
    this.dialog.open(SickLeaveModalComponent, {
      maxHeight: '90vh',
      maxWidth: '120vh',
      data: { SickLeaveId: absenceId },
    });
  }

  shouldShowStatusTooltip(element: AbsenceDto) {
    return element.StatusId === AbsenceStatus.Accepted ||
      element.StatusId === AbsenceStatus.Rejected ||
      element.StatusId === AbsenceStatus.PendingApproval
  }

  getStatusTooltip(element: AbsenceDto): string {
    if (element.IsImported) {
      return this.translateService.instant('AbsenceStatusTooltip.Imported');
    }

    if (!element.ApprovalRequired) {
      return this.translateService.instant('AbsenceStatusTooltip.NoApprovalRequired');
    }

    if (element.StatusId === AbsenceStatus.Accepted) {
      return this.translateService.instant('AbsenceStatusTooltip.HistoryInformation', {
        status: element.Status?.toLowerCase(),
        changedBy: element.AcceptedBy,
        changedDate: this.datePipe.transform(element.AcceptedOn, 'yyyy-MM-dd HH:mm')
      });
    }

    if (element.StatusId === AbsenceStatus.Rejected) {
      return this.translateService.instant('AbsenceStatusTooltip.HistoryInformation', {
        status: element.Status?.toLowerCase(),
        changedBy: element.RejectedBy,
        changedDate: this.datePipe.transform(element.RejectedOn, 'yyyy-MM-dd HH:mm')
      });
    }

    if (element.StatusId === AbsenceStatus.PendingApproval) {
      if (element.LastDecisiveApprovalDate && element.LastDecisiveApprovalUserName) {
        const decisionKey = element.LastDecisiveApprovalIsPositive
          ? 'AbsenceTooltip.AcceptedBy'
          : 'AbsenceTooltip.RejectedBy';

        return this.translateService.instant(decisionKey, {
          userName: element.LastDecisiveApprovalUserName,
          date: this.datePipe.transform(element.LastDecisiveApprovalDate, 'yyyy-MM-dd HH:mm')
        });
      }
    }

    return '';
  }

  private fetchAbsences(): void {
    if (!this.profile?.WorkerId) {
      return;
    }

    if (!this.paginator || !this.sort) {
      return;
    }

    this.dataSource.absencesSubject.next({
      WorkerId: this.profile.WorkerId,
      WorkerAgreementId: this.workerAgreementId,
      Page: this.paginator.pageIndex + 1,
      PageSize: this.paginator.pageSize,
      SortingField: this.sort.active,
      SortingDirection: this.sort.direction,
      AbsenceTypeIds: this.filterTypeIds.value,
      AbsenceStatusIds: this.filterStatusIds.value,
      Year: this.currentYear,
    });
  }

  approvingButtonsVisible(element: AbsenceDto) {
    return element.ApprovalPermissions?.some((permissionDto) => this.authService.hasPermission(permissionDto.Permission as Permission)) || false;
  }
}
